import React, { MouseEventHandler, useEffect, useState } from "react";
import { Connection, ConnectionConfiguration, Member, Provider, ProviderType } from "../common/ObjectTypes";
import SchemaForm from "../common/SchemaForm";
import { ArrowDown, ArrowRight } from "../common/ProjectIcons";
import { getButtonText, getResponseText } from "../common/TestActionFunctions";
import { useTranslation } from "react-i18next";
import { isActionVisible } from "../common/SchemaFormTemplates/checkSchema";
import { inputClasses } from "../common/tailwindClassnames";
import TestAction from "../common/TestAction";
import { useHelp, HelpCard } from "../../context/HelpContext";

type ConnectionDetailsProviderProps = {
    connection: Connection,
    currentMemberIsAdmin: boolean,
    rsjfErrors: any,
    connectionProvider: Provider,
    selectedProviderType: ProviderType,
    onChangeProviderEndpoint: Function,
    onChangeProviderConfigurations: Function,
    onTestAction: Function,
    testActionLoading: boolean,
    testActionResponse: any,
    testActionStatus: number,
    onUpdateConnection: MouseEventHandler,
    onCancelConnectionChanges: MouseEventHandler,
    changesMade: boolean,
}

const ConnectionDetailsProvider = ({
    connection,
    currentMemberIsAdmin,
    rsjfErrors,
    connectionProvider,
    selectedProviderType,
    onChangeProviderEndpoint,
    onChangeProviderConfigurations,
    onTestAction,
    testActionLoading,
    testActionResponse,
    testActionStatus,
    onUpdateConnection,
    onCancelConnectionChanges,
    changesMade,
}: ConnectionDetailsProviderProps) => {
    const { t, i18n } = useTranslation();
    const { setHelpContent } = useHelp();
    const [activeActionId, setActiveActionId] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);

    useEffect(() => {
        const cards: HelpCard[] = [
            {
                title: t('Setting up a Connection in Gundi'),
                description: t('Find step-by-step instructions for connecting your data source.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/connections/' },
            },
            ...(selectedProviderType.help_center_url ? [{
                title: t('{{technology}} Connection Set Up', { technology: selectedProviderType.name }),
                description: t('Configuration guide specific to {{technology}} connections.', { technology: selectedProviderType.name }),
                button: { label: t('Learn More'), url: selectedProviderType.help_center_url },
            }] : []),
        ];
        setHelpContent({ cards });
    }, [selectedProviderType, i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);

    return (
        <div className="h-full mt-3 flex flex-row bg-very-light-gray">
            <div className="flex flex-col pb-16 w-full max-w-content">
                {/* div with text */}
                {/* <div className="flex flex-row justify-between items-center w-full">
                    <label className="text-secondary-gray">{t("The following inputs connect Gundi to the account that provides the data.")}</label>
                </div> */}
                {/* div with configuration details */}
                <div className="flex flex-col w-full pb-52">
                    {/* endpoint, dynamic form */}
                    <div className="flex flex-row">
                        {/* endpoint */}
                        <div className="flex flex-col w-full mr-3 mb-3">
                            {/* <div className="font-bold text-secondary-gray ">Configurations</div> */}
                            <div className="w-full">
                                {/* schema form */}
                                {selectedProviderType.actions.filter((item: any) => item.type !== "push").map((item: any) => {
                                    const uiSchema = item.ui_schema ? { ...item.ui_schema } : {};
                                    const formTitle = item.ui_schema && item.ui_schema['ui:title'] ? item.ui_schema['ui:title'] : item.name;
                                    return (
                                        <div key={item.id} className={isActionVisible(item) ? "mb-[16px] rounded p-[16px]" : "hidden"}>
                                            <div className="mb-[16px] flex flex-col items-start">
                                                {item.schema.hasOwnProperty('is_executable') && item.schema.is_executable === true ? (
                                                    <TestAction
                                                        actionId={item.id}
                                                        actionValue={item.value}
                                                        onTestAction={(id: any, value: any) => {
                                                            onTestAction(id, value);
                                                            setActiveActionId(id);
                                                        }}
                                                        isLoading={testActionLoading && activeActionId === item.id}
                                                        response={activeActionId === item.id ? testActionResponse : null}
                                                        buttonText={getButtonText(item.value, t)}
                                                        formTitle={formTitle}
                                                        uiSchema={uiSchema}
                                                    />
                                                ) : (
                                                    <div className="text-2xl font-normal text-secondary-gray transition-all duration-300">{formTitle}</div>
                                                )}
                                                {uiSchema['ui:description'] && (
                                                    <div className="form-root-description !mb-0 text-gray-600 mt-1 transition-colors duration-300">
                                                        {uiSchema['ui:description']}
                                                    </div>
                                                )}
                                            </div>
                                            {item?.schema?.properties && Object.keys(item.schema.properties).length > 0 && (
                                                <div className="rounded border border-field-outline bg-white p-[16px] transition-colors duration-300">
                                                    <SchemaForm
                                                        userIsAdmin={currentMemberIsAdmin}
                                                        formTitle=""
                                                        schema={item.schema}
                                                        ui_schema={{...uiSchema, 'ui:description': ''}}
                                                        formdata={connectionProvider.configurations && connectionProvider.configurations.findIndex((a: ConnectionConfiguration) => a.action.id === item.id) !== -1 ? connectionProvider.configurations.find((a: ConnectionConfiguration) => a.action.id === item.id).data : {}}
                                                        onChange={(e: any) => onChangeProviderConfigurations(e, item.id)}
                                                        isEditable={true}
                                                        errors={rsjfErrors[item.id]}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* advanced configurations */}
                                <div>
                                    <div className="mt-6 text-lg font-semibold text-secondary-gray cursor-pointer" onClick={() => setShowAdvanced(!showAdvanced)}>
                                        {showAdvanced ? <ArrowDown /> : <ArrowRight />} {t("Advanced Configurations")}
                                    </div>
                                    {showAdvanced &&
                                        <div className="ml-8 flex flex-col pb-14">
                                            <label
                                                className="inline-block hover:cursor-pointer text-sm text-secondary-gray"
                                            >
                                                Optional Endpoint (For Advanced Users)
                                            </label>
                                            <input
                                                id="endpoint"
                                                placeholder=""
                                                value={connectionProvider.base_url}
                                                className={inputClasses.standard + " mb-3"}
                                                onChange={(e) => onChangeProviderEndpoint(e)}
                                                disabled={false}
                                            >
                                            </input>
                                        </div>}
                                </div>

                            </div>
                        </div>
                    </div>

                </div>
            </div>
            {/* buttons */}
            <div className="w-full z-10 fixed bottom-0 mt-12 mr-6 py-3 w-full flex flex-row bg-white/10 backdrop-blur">
                <button className={changesMade ? "bg-dark-green hover:bg-status-green text-white px-8 py-2 font-bold rounded mr-3" :
                    "bg-light-green text-medium-gray px-8 py-2 font-bold rounded mr-3"}
                    disabled={!changesMade}
                    onClick={onUpdateConnection}>
                    Save
                </button>
                <button className={"text-secondary-gray font-bold px-3 mx-3"}
                    onClick={onCancelConnectionChanges}
                >
                    Cancel
                </button>
            </div>
        </div>
    )
};

export default ConnectionDetailsProvider;